/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import EmObject, { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import pluralize from 'cio/utils/pluralize';
import {
  getTimeFrameUnitsForMaxUnit,
  roundTimeframe,
  findRoundUnit,
  countToSeconds,
} from 'cio/utils/round-timeframe-units';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';

interface UnitOfTimeInputArgs {
  timeframeSeconds: number;
  maxTimeframe?: number;
  maxTimeframeUnit?: string;
  disabled?: boolean;
  allowZeroValue?: boolean;
  displayMaxTimeframeHelpText?: boolean;
  onChange: (second: number) => unknown;
  onValidate: (valid: boolean) => unknown;
  hasReadOnlyPermission: boolean;
}

class TimeframeUnitItem extends EmObject {
  value!: string;
  context!: UnitOfTimeInput;

  @computed('value', 'context.timeframeInputValue')
  get label() {
    return pluralize(this.context.timeframeInputValue, this.value);
  }
}

export default class UnitOfTimeInput extends Component<UnitOfTimeInputArgs> {
  @tracked timeframeInputValue = 0;
  @tracked timeframeUnit = '';

  get timeframeUnits() {
    return getTimeFrameUnitsForMaxUnit(this.args.maxTimeframeUnit);
  }

  get timeframeUnitItems(): TimeframeUnitItem[] {
    return this.timeframeUnits.map(({ value }) => {
      return TimeframeUnitItem.create({
        value,
        context: this,
      });
    });
  }

  get timeframeErrors() {
    if (isNaN(this.timeframeInputValue)) {
      return ['Timeframe must be a number'];
    } else if (this.args.allowZeroValue !== true && this.timeframeInputValue <= 0) {
      return ['Timeframe must be greater than 0'];
    } else if (this.timeframeInputValue < 0) {
      return ['Timeframe must be less than 0'];
    } else if (
      this.args.maxTimeframe != null &&
      countToSeconds(this.timeframeInputValue, this.timeframeUnit) > this.args.maxTimeframe
    ) {
      return [`Timeframe can't be greater than ${this.maxTimeframeText}`];
    } else {
      return [];
    }
  }

  get maxTimeframeText() {
    if (this.args.maxTimeframe == null) {
      return '';
    }

    let unit = findRoundUnit(this.args.maxTimeframe, this.timeframeUnits);
    let timeframe = Math.ceil(this.args.maxTimeframe / unit.seconds);

    return `${formatNumberAndPluralize([timeframe, unit.value])}`;
  }

  get hasError() {
    return !isEmpty(this.timeframeErrors);
  }

  @action
  calculateUnit() {
    let timeframe = roundTimeframe(this.args.timeframeSeconds);
    let unit = findRoundUnit(timeframe, this.timeframeUnits);
    let timeframeInputValue = Math.ceil(timeframe / unit.seconds);

    this.timeframeInputValue = timeframeInputValue;
    this.timeframeUnit = unit.value;
  }

  @action
  handleValueChange(value: string) {
    this.timeframeInputValue = parseInt(value);

    let { timeframeUnit: unit } = this;
    let seconds = countToSeconds(this.timeframeInputValue, unit);
    this.args.onChange(seconds);

    let isInputValid = !this.hasError;
    this.args.onValidate(isInputValid);
  }

  @action
  onValueChange(event: FlyKeyboardEvent<HTMLInputElement>) {
    this.handleValueChange(event.target.value);
  }

  @action
  onUnitChange(unit: string) {
    let { timeframeInputValue: value } = this;
    let seconds = countToSeconds(value, unit);

    this.args.onChange(seconds);
    this.args.onValidate(!this.hasError);
  }
}
